import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid";
import { ReiceptItem } from "pages/receipt/components/ReiceptItem";
import { isNotPaid } from "pages/receipt/utils/isNotPayed";
import React, { ChangeEvent, FC, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useLocation } from "react-router-dom";
import { bulkPayReceipts, getSelectedReiceptsByPeriod } from "services/receipt";

const PaymentPage: FC = () => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const from = queryParams.get("from");
  const to = queryParams.get("to");

  const queryClient = useQueryClient();
  const [totalAmountPaid, setTotalAmountPaid] = useState<number>(0);
  const [openDialog, setOpenDialog] = useState<boolean>(false);


  const { data, isLoading, error } = useQuery(
    ["receipts", from, to],
    () =>
      getSelectedReiceptsByPeriod({
        query: { from: String(from), to: String(to) },
      }),
    { enabled: !!from && !!to }
  );

  const [selectedReceipts, setSelectedReceipts] = useState<{
    [key: string]: any[];
  }>({});

  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const handleDayCheckboxChange = (day: string) => {
    const newSelectedReceipts = { ...selectedReceipts };
    if (newSelectedReceipts[day]) {
      delete newSelectedReceipts[day];
    } else {
      const dayData = data?.data?.find((d: any) => d.day === day);
      if (dayData) {
        newSelectedReceipts[day] =
          dayData.receipts?.filter((receipt: any) =>
            isNotPaid(receipt.amount_received, receipt.amount_given)
          ) || [];
      }
    }
    setSelectedReceipts(newSelectedReceipts);
  };

  const handleReceiptCheckboxChange = (receipt: any, day: string) => {
    const newSelectedReceipts = { ...selectedReceipts };
    if (newSelectedReceipts[day]?.includes(receipt)) {
      newSelectedReceipts[day] = newSelectedReceipts[day].filter(
        (r: any) => r !== receipt
      );
      if (newSelectedReceipts[day].length === 0) {
        delete newSelectedReceipts[day];
      }
    } else {
      if (isNotPaid(receipt.amount_received, receipt.amount_given)) {
        newSelectedReceipts[day] = [
          ...(newSelectedReceipts[day] || []),
          receipt,
        ];
      }
    }
    setSelectedReceipts(newSelectedReceipts);
  };

  const bulkPayMutation = useMutation(bulkPayReceipts, {
    onSuccess: (data) => {
      console.log("Success");
      queryClient.refetchQueries({ queryKey: ["receipts", from, to] });

      setTotalAmountPaid(data?.data?.totalDifference);

      setOpenDialog(true);
      setSelectedReceipts({});
      setSearchTerm("");
    },
    onError: () => {
      console.log("Error");
    },
  });

  const autoPay = () => {
    bulkPayMutation.mutate(selectedReceipts);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  if (isLoading) return <CircularProgress />;
  if (error) return <Typography color="error">Error</Typography>;

  const filteredData = data?.data?.map((day: any) => ({
    ...day,
    receipts: day.receipts.filter((receipt: any) =>
      receipt.receipt_number?.toLowerCase().includes(searchTerm.toLowerCase())
    ),
  }));


  return (
    <Box>
      <Typography variant="h4">
        Receipts from {from} to {to}
      </Typography>
      <TextField
        label="Search Receipt Number"
        // variant="standard"
        size="medium"
        fullWidth
        margin="normal"
        type="number"
        sx={{
          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
            {
              display: "none",
            },
          "& input[type=number]": {
            MozAppearance: "textfield",
          },
          backgroundColor: "white",
        }}
        placeholder="Search Receipt Number"
        value={searchTerm}
        onChange={handleSearchChange}
      />
      <Button
        variant="contained"
        color="primary"
        onClick={autoPay}
        disabled={
          bulkPayMutation.isLoading ||
          Object.keys(selectedReceipts).length === 0
        }
      >
        Auto Pay Selected Receipts
      </Button>
      <TableContainer
        component={Paper}
        sx={{ marginTop: 2, backgroundColor: "white" }}
      >
        <Table>
          <TableBody>
            {filteredData?.map((day: any) => (
              <React.Fragment key={day.day}>
                <TableRow>
                  <TableCell>
                    <Checkbox
                      checked={!!selectedReceipts[day.day]}
                      onChange={() => handleDayCheckboxChange(day.day)}
                    />
                  </TableCell>
                  <TableCell
                    align="left"
                    width="100%"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedDay(expandedDay === day.day ? null : day.day);
                    }}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="h6" textAlign="left">
                        Day: {day.day}
                      </Typography>
                      <IconButton
                        onClick={() =>
                          setExpandedDay(
                            expandedDay === day.day ? null : day.day
                          )
                        }
                        aria-expanded={expandedDay === day.day}
                        aria-label={
                          expandedDay === day.day ? "Collapse" : "Expand"
                        }
                      >
                        {expandedDay === day.day ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={7}>
                    <Collapse in={expandedDay === day.day}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Select</TableCell>
                            <TableCell>Client ID</TableCell>
                            <TableCell>Containers</TableCell>
                            <TableCell>Receipt Number</TableCell>
                            <TableCell>Amount Received</TableCell>
                            <TableCell>Amount Given</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {day.receipts.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center">
                                No receipts available
                              </TableCell>
                            </TableRow>
                          ) : (
                            day.receipts.map((receipt: any) => (
                              <ReiceptItem
                                key={receipt.id}
                                day={day}
                                receipt={receipt}
                                handleReceiptCheckboxChange={
                                  handleReceiptCheckboxChange
                                }
                                selectedReceipts={selectedReceipts}
                              />
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        sx={{}}
        PaperProps={{
          sx: {
            width: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography fontWeight={"bold"}>Total Amount Paid</Typography>
          <IconButton onClick={handleCloseDialog}>
            <GridCloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6">You pay: {totalAmountPaid} TND</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentPage;
